// src/components/admin/DailyDuties.jsx
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Printer, RefreshCw, Edit2, Check, X } from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getAllStaffIncludingPaused } from '../../services/staffService';

// ─────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────
const DEFAULT_BREAK_TIMES = {
  morning:   ['10:30 AM', '10:40 AM', '10:50 AM', '11:00 AM', '11:10 AM', '11:20 AM', '11:30 AM', '11:40 AM', '11:50 AM', '12:00 PM'],
  lunch:     ['12:00 PM', '12:15 PM', '12:30 PM', '12:45 PM', '1:00 PM',  '1:15 PM',  '1:30 PM',  '1:45 PM',  '2:00 PM',  '2:15 PM'],
  afternoon: ['2:30 PM',  '2:40 PM',  '2:50 PM',  '3:00 PM',  '3:10 PM',  '3:20 PM',  '3:30 PM',  '3:40 PM',  '3:50 PM',  '4:00 PM'],
  halfShift: ['3:00 PM',  '3:10 PM',  '3:20 PM',  '3:30 PM',  '3:40 PM',  '3:50 PM',  '4:00 PM',  '4:10 PM',  '4:20 PM',  '4:30 PM'],
};

const THROUGHOUT_DUTIES = [
  { id: 'wipe',     label: 'Wipe down tables',      note: 'After every snack and after lunch' },
  { id: 'bathroom', label: 'Bathroom check + clean', note: 'Before each snack break and before lunch' },
];

const CLOSING_DUTIES = [
  { id: 'sweep',    label: 'Sweep & Vacuum' },
  { id: 'mop',      label: 'Mopping' },
  { id: 'trash',    label: 'Take out trash' },
  { id: 'bathroom', label: 'Bathroom deep clean + restock supplies — end of day' },
];

const LEAD_DUTIES = [
  'Plan and prepare materials for tomorrow (binders, lesson plans, supplies)',
];

const LOCATIONS = [
  { id: 'redmond',  label: 'Stemtree Redmond',  color: '#3b82f6', bg: '#eff6ff' },
  { id: 'kirkland', label: 'Stemtree Kirkland', color: '#059669', bg: '#f0fdf4' },
];

const fmtDate = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const y = d.getFullYear();
  return `${m}-${day}-${y}`;
};

const fmtDisplay = (d) =>
  d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

const dayOfYear = (d) => Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 86400000);

// ─────────────────────────────────────────────────────────────
// DUTY ENGINE
// ─────────────────────────────────────────────────────────────
function assignDuties(staffList, dateObj, leadStaffId) {
  const n = staffList.length;
  if (n === 0) return null;

  const doy = dayOfYear(dateObj);

  // Classify staff
  const full         = staffList.filter(s => !s.isHalfShift);
  const halfMorning  = staffList.filter(s => s.isHalfShift && (s.startHour ?? 9)  < 12);
  const halfAfternoon= staffList.filter(s => s.isHalfShift && (s.startHour ?? 13) >= 12);
  const half         = staffList.filter(s => s.isHalfShift);

  // ── Throughout the Day duties ──────────────────────────────
  // Eligible: full-shift staff only (they cover all snack + lunch + EOD)
  // Half-day staff get their time-limited duties listed separately below
  // Exclude lead staff from Throughout duties — they have their own responsibilities
  const throughoutPool = (full.length > 0 ? full : staffList)
    .filter(s => s.id !== leadStaffId);
  const throughoutPool2 = throughoutPool.length > 0 ? throughoutPool : (full.length > 0 ? full : staffList);
  const rotT = doy % throughoutPool2.length;
  const throughoutPerson = throughoutPool2[rotT];

  // ── Closing duties ─────────────────────────────────────────
  // Eligible: full-shift + afternoon half (present at EOD)
  // Always include throughoutPerson if needed to spread duties across people
  const closingAll = [...full, ...halfAfternoon];
  const closingWithout = closingAll.filter(s => s.id !== throughoutPerson.id);
  // If excluding throughoutPerson leaves fewer people than duties, include them
  const closingEligible = closingWithout.length >= Math.min(CLOSING_DUTIES.length, closingAll.length)
    ? closingWithout
    : closingAll;
  const closingN = closingEligible.length;

  const closingAssignments = {};
  if (closingN > 0) {
    const rotC = doy % closingN;
    const rotatedClosing = [...closingEligible.slice(rotC), ...closingEligible.slice(0, rotC)];
    CLOSING_DUTIES.forEach((duty, i) => {
      const person = rotatedClosing[i % closingN];
      if (!closingAssignments[person.id]) closingAssignments[person.id] = [];
      closingAssignments[person.id].push(duty);
    });
  }

  // ── Half-day duty notes ────────────────────────────────────
  // Morning half: responsible for morning snack cleanup + bathroom check before snack/lunch
  // Afternoon half: responsible for afternoon snack cleanup + bathroom check before afternoon snack
  const halfDuties = {};
  halfMorning.forEach(s => {
    halfDuties[s.id] = [
      'Wipe down tables after morning snack',
      'Bathroom check before morning snack',
      'Wipe down tables after lunch (if still present)',
    ];
  });
  halfAfternoon.forEach(s => {
    halfDuties[s.id] = [
      'Wipe down tables after afternoon snack',
      'Bathroom check before afternoon snack',
    ];
  });

  const allRotated = [...full, ...halfAfternoon, ...halfMorning];

  return {
    throughoutPerson,
    closingAssignments,
    halfDuties,
    halfMorning,
    halfAfternoon,
    rotated: allRotated,
    morningBreaks:   full.map((p, i) => ({ ...p, time: DEFAULT_BREAK_TIMES.morning[i]   || '—' })),
    lunchBreaks:     full.map((p, i) => ({ ...p, time: DEFAULT_BREAK_TIMES.lunch[i]     || '—' })),
    afternoonBreaks: full.map((p, i) => ({ ...p, time: DEFAULT_BREAK_TIMES.afternoon[i] || '—' })),
    halfBreaks:      half.map((p, i) => ({ ...p, time: DEFAULT_BREAK_TIMES.halfShift[i] || '—' })),
  };
}

// ─────────────────────────────────────────────────────────────
// LOCATION PANEL — one per location
// ─────────────────────────────────────────────────────────────
const LocationPanel = ({ locConfig, allStaff, dateStr, dateObj }) => {
  const [scheduledIds, setScheduledIds]   = useState([]);
  const [manualIds, setManualIds]         = useState(null); // null = use schedule
  const [halfShiftOverrides, setHalfShiftOverrides] = useState({});
  const [breakOverrides, setBreakOverrides] = useState({});
  const [editingBreak, setEditingBreak]   = useState(null);
  const [editingVal, setEditingVal]       = useState('');
  const [duties, setDuties]               = useState(null);
  const [loading, setLoading]             = useState(false);
  const [leadStaffId, setLeadStaffId]     = useState(null);
  const printRef = useRef();

  // All non-operations staff available for selection
  // Actual location filtering happens via schedule's location field
  const locationStaff = allStaff;

  useEffect(() => { loadSchedule(); }, [dateStr, allStaff]);

  const loadSchedule = async () => {
    if (allStaff.length === 0) return;
    setLoading(true);
    try {
      // Try MM-DD-YYYY format first, then MM-DD-YY as fallback
      const [m, d, y] = dateStr.split('-');
      const dateKeyFull = dateStr; // MM-DD-YYYY
      const dateKeyShort = `${m}-${d}-${y.slice(-2)}`; // MM-DD-YY

      const tryQuery = async (dateKey) => {
        const snap = await getDocs(
          query(collection(db, 'schedules'), where('date', '==', dateKey))
        );
        return snap.docs;
      };

      let docs = await tryQuery(dateKeyFull);
      if (docs.length === 0) docs = await tryQuery(dateKeyShort);

      const seen = new Set();
      const ids = [];
      const autoHalfShift = {};

      const parseHour = (t) => {
        if (!t) return null;
        const m = String(t).toUpperCase().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/);
        if (!m) return null;
        let h = parseInt(m[1]), min = parseInt(m[2]);
        if (m[3] === 'PM' && h !== 12) h += 12;
        if (m[3] === 'AM' && h === 12) h = 0;
        return h + min / 60;
      };

      docs.forEach(d => {
        const data = d.data();
        const staffId = data.staffId;
        const schedLocRaw = (data.location || data.locationId || 'redmond').toLowerCase();
        // Handle both short ('kirkland','redmond') and full ('stemtree kirkland') forms
        const isKirkland = schedLocRaw.includes('kirkland') || schedLocRaw === 'kl';
        const isRedmond = !isKirkland;
        const locMatch = (locConfig.id === 'kirkland' && isKirkland) ||
                         (locConfig.id === 'redmond' && isRedmond);
        const staff = allStaff.find(s => s.id === staffId);
        if (staff && locMatch && !seen.has(staffId)) {
          seen.add(staffId);
          ids.push(staffId);
          // Store start hour on staff object for duty eligibility
          const start = parseHour(data.startTime);
          const end = parseHour(data.endTime);
          if (start !== null) {
            const staffObj = allStaff.find(s => s.id === staffId);
            if (staffObj) staffObj._startHour = start;
          }
          if (start !== null && end !== null) {
            const hoursWorked = end - start;
            autoHalfShift[staffId] = hoursWorked < 6;
            // Store shift start for smart break time calculation
            if (hoursWorked < 6) {
              // Break ~2hrs into shift, rounded to nearest 5 min
              const breakHour = start + 2;
              const bh = Math.floor(breakHour);
              const bm = Math.round((breakHour % 1) * 60 / 5) * 5;
              const ampm = bh >= 12 ? 'PM' : 'AM';
              const displayH = bh > 12 ? bh - 12 : (bh === 0 ? 12 : bh);
              const displayM = String(bm).padStart(2, '0');
              autoHalfShift[staffId + '_breakTime'] = `${displayH}:${displayM} ${ampm}`;
            }
          }
        }
      });

      setScheduledIds(ids);
      setLeadStaffId(null); // reset lead on reload
      // Auto-set half shift overrides from schedule hours (only if not manually overridden)
      // Always set half-shift from schedule on reload
      const halfShiftMap = {};
      Object.entries(autoHalfShift).forEach(([k, v]) => {
        if (!k.includes('_breakTime')) halfShiftMap[k] = v;
      });
      setHalfShiftOverrides(halfShiftMap);
      // Store per-staff break times
      const breakMap = {};
      Object.entries(autoHalfShift).forEach(([k, v]) => {
        if (k.includes('_breakTime')) {
          const staffId = k.replace('_breakTime', '');
          breakMap[`${locConfig.id}-half-0-${staffId}`] = v;
        }
      });
      setBreakOverrides(prev => ({ ...prev, ...breakMap }));
      setManualIds(null);
    } catch (err) {
      console.error(err);
      setScheduledIds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { generate(); }, [scheduledIds, manualIds, halfShiftOverrides, leadStaffId]);

  const generate = () => {
    const activeIds = manualIds !== null ? manualIds : scheduledIds;
    const staffToUse = allStaff
      .filter(s => activeIds.includes(s.id))
      .map(s => ({
        ...s,
        isHalfShift: halfShiftOverrides[s.id] ?? s.isHalfShift ?? false,
        startHour: s._startHour ?? 9,
      }));
    setDuties(assignDuties(staffToUse, dateObj, leadStaffId));
  };

  const toggleStaff = (id) => {
    const current = manualIds !== null ? manualIds : scheduledIds;
    setManualIds(current.includes(id) ? current.filter(x => x !== id) : [...current, id]);
  };

  const toggleHalf = (id) => setHalfShiftOverrides(p => ({ ...p, [id]: !(p[id] ?? false) }));

  const getBreakTime = (key, def) => breakOverrides[key] ?? def;

  const handlePrint = () => {
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>${locConfig.label} — ${dateStr}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; font-size: 12px; margin: 0.75in 0.75in 0.5in 0.75in; color: #111; }
        /* Reset all inline styles from React component */
        div[style], td[style], p[style], span[style] { all: revert; font-family: Arial, sans-serif; }
        h1 { font-size: 17px; font-weight: 900; margin: 0 0 2px; color: #1f2937; }
        h2 { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.07em;
             color: #1f2937; margin: 12px 0 4px; border-bottom: 2px solid #1f2937; padding-bottom: 3px; }
        p { font-size: 11px; color: #374151; margin: 0 0 6px; line-height: 1.4; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
        th { background: #1f2937 !important; color: white !important; font-size: 10px; font-weight: 700;
             text-align: left; padding: 5px 8px; border: 1px solid #374151; }
        td { padding: 5px 8px; border: 1px solid #e5e7eb; font-size: 11px; vertical-align: top; color: #111; }
        td b, td strong { font-weight: 700; }
        .lead-box { background: #fef3c7; border: 2px solid #f59e0b; border-radius: 6px; padding: 10px 12px; margin-bottom: 10px; }
        .lead-box h3 { font-size: 12px; font-weight: 800; color: #92400e; margin: 0 0 4px; }
        .lead-box li { font-size: 11px; color: #374151; margin: 2px 0 2px 16px; }
        .footer { font-size: 9px; color: #9ca3af; margin-top: 12px; border-top: 1px solid #e5e7eb; padding-top: 5px; }
        @page { margin: 0.5in; }
      </style></head>
      <body>
        <h1>${locConfig.label} — Daily Duties</h1>
        <p class="sub">${fmtDisplay(dateObj)}</p>
        ${leadStaffId ? (() => {
          const lead = locationStaff.find(s => s.id === leadStaffId);
          if (!lead) return '';
          return '<div class="lead-box"><h3>⭐ Lead Staff: ' + lead.firstName + ' ' + lead.lastName + '</h3><ul>' +
            LEAD_DUTIES.map(d => '<li>' + d + '</li>').join('') + '</ul></div>';
        })() : ''}
        ${printRef.current.innerHTML}
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  const activeIds = manualIds !== null ? manualIds : scheduledIds;

  const st = {
    th: { padding: '0.5rem 0.75rem', backgroundColor: '#1f2937', fontWeight: '800', fontSize: '0.72rem', color: 'white', textAlign: 'left', border: '1px solid #374151' },
    td: { padding: '0.5rem 0.75rem', verticalAlign: 'top', border: '1px solid #e5e7eb', fontSize: '0.8rem' },
    tdBold: { padding: '0.5rem 0.75rem', verticalAlign: 'top', border: '1px solid #e5e7eb', fontSize: '0.8rem', fontWeight: '700' },
  };

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', border: `2px solid ${locConfig.color}30`, overflow: 'hidden', marginBottom: '1.5rem' }}>
      {/* Location header */}
      <div style={{ backgroundColor: locConfig.bg, borderBottom: `2px solid ${locConfig.color}30`, padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '1rem', fontWeight: '800', color: locConfig.color }}>📍 {locConfig.label}</div>
          <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.1rem' }}>
            {activeIds.length} staff on duty · {fmtDisplay(dateObj)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={loadSchedule} title="Reload from schedule"
            style={{ padding: '0.35rem 0.6rem', border: '1px solid #e5e7eb', borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.3rem', color: '#374151' }}>
            <RefreshCw size={12} /> Reload
          </button>
          <button onClick={handlePrint} disabled={!duties}
            style={{ padding: '0.35rem 0.75rem', border: 'none', borderRadius: '6px', backgroundColor: duties ? locConfig.color : '#9ca3af', color: 'white', cursor: duties ? 'pointer' : 'not-allowed', fontSize: '0.72rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <Printer size={12} /> Print
          </button>
        </div>
      </div>

      <div style={{ padding: '0.85rem 1rem' }}>
        {/* Staff selector */}
        <div style={{ marginBottom: '0.85rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>
            Staff {manualIds !== null && <span style={{ color: '#f59e0b' }}>· manual override</span>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.4rem' }}>
            {locationStaff.map(s => {
              const inList = activeIds.includes(s.id);
              const isHalf = halfShiftOverrides[s.id] ?? s.isHalfShift ?? false;
              return (
                <div key={s.id} onClick={() => toggleStaff(s.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.25rem 0.5rem', borderRadius: '5px', border: `1.5px solid ${inList ? locConfig.color : '#e5e7eb'}`, backgroundColor: inList ? locConfig.bg : '#f9fafb', cursor: 'pointer', fontSize: '0.75rem', fontWeight: inList ? '700' : '400', color: inList ? locConfig.color : '#9ca3af' }}>
                  {inList ? '✅' : '○'} {s.firstName} {s.lastName?.charAt(0)}.
                  {inList && (
                    <span onClick={e => { e.stopPropagation(); toggleHalf(s.id); }}
                      style={{ marginLeft: '0.2rem', fontSize: '0.62rem', fontWeight: '700', padding: '0.05rem 0.3rem', borderRadius: '3px', backgroundColor: isHalf ? '#fef3c7' : '#d1fae5', color: isHalf ? '#92400e' : '#065f46' }}>
                      {isHalf ? '½' : 'Full'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {/* Lead Staff Selector */}
          {activeIds.length > 0 && (
            <div style={{ marginTop: '0.6rem', paddingTop: '0.6rem', borderTop: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>
                ⭐ Lead Staff
              </div>
              <select
                value={leadStaffId || ''}
                onChange={e => setLeadStaffId(e.target.value || null)}
                style={{ width: '100%', padding: '0.4rem 0.6rem', border: `1.5px solid ${leadStaffId ? '#f59e0b' : '#e5e7eb'}`, borderRadius: '6px', fontSize: '0.8rem', backgroundColor: leadStaffId ? '#fffbeb' : 'white', color: '#374151', fontWeight: leadStaffId ? '700' : '400' }}
              >
                <option value="">— Select lead staff —</option>
                {locationStaff.filter(s => activeIds.includes(s.id) && !s.isHalfShift).map(s => (
                  <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>
                ))}
              </select>
              {leadStaffId && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: '#92400e', backgroundColor: '#fef3c7', padding: '0.35rem 0.6rem', borderRadius: '4px' }}>
                  ⭐ Lead duties: Plan materials for tomorrow · Final walkthrough · Ensure center is clean
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '0.5rem' }}>
            <span style={{ fontSize: '0.68rem', color: '#9ca3af' }}>Click staff to toggle · click Full/½ for shift type</span>
            {manualIds !== null && (
              <button onClick={() => { setManualIds(null); }}
                style={{ fontSize: '0.68rem', padding: '0.1rem 0.4rem', border: '1px solid #e5e7eb', borderRadius: '3px', cursor: 'pointer', backgroundColor: 'white', color: '#6b7280' }}>
                Reset to schedule
              </button>
            )}
          </div>
        </div>

        {loading && <div style={{ color: '#9ca3af', fontSize: '0.82rem', padding: '1rem 0' }}>Loading schedule...</div>}

        {/* Printable content */}
        {duties && (
          <div ref={printRef}>
            {/* Print-only header */}
            <h1 style={{ display: 'none' }}>{locConfig.label}</h1>
            <p className="sub" style={{ display: 'none' }}>{fmtDisplay(dateObj)}</p>

            {/* Break Schedule */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: '900', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem', paddingBottom: '0.35rem', borderBottom: '2px solid #1f2937' }}>
                ⏰ Break Schedule
              </div>

              {duties.morningBreaks.length > 0 && (
                <>
                  <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Full Shift (9 AM – 5 PM)</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0.5rem' }}>
                    <thead>
                      <tr>
                        <th style={st.th}>Staff</th>
                        <th style={st.th}>Morning Break (10 min)</th>
                        <th style={st.th}>Lunch (30 min)</th>
                        <th style={st.th}>Afternoon Break (10 min)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {duties.morningBreaks.map((p, i) => (
                        <tr key={p.id}>
                          <td style={st.tdBold}>{p.firstName} {p.lastName}</td>
                          {['morning', 'lunch', 'afternoon'].map(period => {
                            const key = `${locConfig.id}-${period}-${i}`;
                            const def = DEFAULT_BREAK_TIMES[period][i] || '—';
                            const val = getBreakTime(key, def);
                            return (
                              <td key={period} style={st.td}>
                                {editingBreak === key ? (
                                  <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                    <input value={editingVal} onChange={e => setEditingVal(e.target.value)}
                                      style={{ width: '75px', padding: '0.15rem 0.3rem', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '0.75rem' }} autoFocus />
                                    <button onClick={() => { setBreakOverrides(p => ({ ...p, [key]: editingVal })); setEditingBreak(null); }}
                                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#059669', padding: '0.1rem' }}><Check size={12} /></button>
                                    <button onClick={() => setEditingBreak(null)}
                                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626', padding: '0.1rem' }}><X size={12} /></button>
                                  </div>
                                ) : (
                                  <span onClick={() => { setEditingBreak(key); setEditingVal(val); }}
                                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                    {val}
                                    {breakOverrides[key] && <span style={{ fontSize: '0.6rem', backgroundColor: '#fef3c7', color: '#92400e', padding: '0 0.25rem', borderRadius: '2px' }}>edited</span>}
                                    <Edit2 size={9} style={{ color: '#d1d5db' }} />
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {duties.halfBreaks.length > 0 && (
                <>
                  <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Half Shift (1 PM – 5 PM)</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0.5rem' }}>
                    <thead><tr><th style={st.th}>Staff</th><th style={st.th}>Break (10 min)</th></tr></thead>
                    <tbody>
                      {duties.halfBreaks.map((p, i) => {
                        const key = `${locConfig.id}-half-0-${p.id}`;
                        const fallbackKey = `${locConfig.id}-half-${i}`;
                        const val = getBreakTime(key, getBreakTime(fallbackKey, DEFAULT_BREAK_TIMES.halfShift[i] || '—'));
                        return (
                          <tr key={p.id}>
                            <td style={st.tdBold}>{p.firstName} {p.lastName}</td>
                            <td style={st.td}>
                              {editingBreak === key ? (
                                <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                  <input value={editingVal} onChange={e => setEditingVal(e.target.value)}
                                    style={{ width: '75px', padding: '0.15rem 0.3rem', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '0.75rem' }} autoFocus />
                                  <button onClick={() => { setBreakOverrides(p => ({ ...p, [key]: editingVal })); setEditingBreak(null); }}
                                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#059669' }}><Check size={12} /></button>
                                  <button onClick={() => setEditingBreak(null)}
                                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626' }}><X size={12} /></button>
                                </div>
                              ) : (
                                <span onClick={() => { setEditingBreak(key); setEditingVal(val); }}
                                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                  {val} <Edit2 size={9} style={{ color: '#d1d5db' }} />
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </div>

            {/* Throughout the Day */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: '900', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem', paddingBottom: '0.35rem', borderBottom: '2px solid #1f2937' }}>
                🔄 Throughout the Day Duties
              </div>
              <div style={{ fontSize: '0.78rem', color: '#374151', marginBottom: '0.4rem' }}>
                Assigned to <strong>{duties.throughoutPerson.firstName} {duties.throughoutPerson.lastName}</strong> today
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={st.th}>Duty</th><th style={st.th}>When</th></tr></thead>
                <tbody>
                  {THROUGHOUT_DUTIES.map(d => (
                    <tr key={d.id}>
                      <td style={st.tdBold}>{d.label}</td>
                      <td style={{ ...st.td, color: '#6b7280' }}>{d.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Half-Day Staff Duties */}
            {(duties.halfMorning.length > 0 || duties.halfAfternoon.length > 0) && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '900', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem', paddingBottom: '0.35rem', borderBottom: '2px solid #1f2937' }}>
                  🕐 Half-Day Staff Duties
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={st.th}>Staff</th>
                    <th style={st.th}>Shift</th>
                    <th style={st.th}>Assigned Duties</th>
                  </tr></thead>
                  <tbody>
                    {[...duties.halfMorning, ...duties.halfAfternoon].map(person => (
                      <tr key={person.id}>
                        <td style={st.tdBold}>{person.firstName} {person.lastName}</td>
                        <td style={{ ...st.td, color: '#6b7280', fontSize: '0.72rem' }}>
                          {duties.halfMorning.find(s => s.id === person.id) ? '🌅 Morning' : '🌆 Afternoon'}
                        </td>
                        <td style={st.td}>
                          {(duties.halfDuties[person.id] || []).map((d, i) => (
                            <div key={i}>• {d}</div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Lead Staff Section */}
            {leadStaffId && locationStaff.find(s => s.id === leadStaffId) && (
              <div style={{ marginBottom: '1rem', backgroundColor: '#fffbeb', border: '2px solid #f59e0b', borderRadius: '8px', padding: '0.75rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: '900', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem', paddingBottom: '0.35rem', borderBottom: '2px solid #f59e0b' }}>
                  ⭐ Lead Staff — {locationStaff.find(s => s.id === leadStaffId).firstName} {locationStaff.find(s => s.id === leadStaffId).lastName}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={{ ...st.th, backgroundColor: '#f59e0b', border: '1px solid #d97706' }}>Lead Duties</th>
                  </tr></thead>
                  <tbody>
                    {LEAD_DUTIES.map((d, i) => (
                      <tr key={i}><td style={st.td}>• {d}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Closing Duties */}
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: '900', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem', paddingBottom: '0.35rem', borderBottom: '2px solid #1f2937' }}>
                🔒 Closing Duties
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={st.th}>Staff</th><th style={st.th}>Assigned Tasks</th></tr></thead>
                <tbody>
                  {duties.rotated.map(person => {
                    const assigned = duties.closingAssignments[person.id] || [];
                    if (!assigned.length) return null;
                    return (
                      <tr key={person.id}>
                        <td style={st.tdBold}>{person.firstName} {person.lastName}</td>
                        <td style={st.td}>{assigned.map(d => <div key={d.id}>• {d.label}</div>)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid #f3f4f6' }}>
              Duties rotate daily. Break times staggered for coverage — please return on time.
            </div>
          </div>
        )}

        {!duties && !loading && activeIds.length === 0 && (
          <div style={{ textAlign: 'center', padding: '1.5rem', color: '#d1d5db', fontSize: '0.82rem' }}>
            No staff selected — click names above to add staff
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────
const DailyDuties = ({ onBack }) => {
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(fmtDate(today));
  const [allStaff, setAllStaff] = useState([]);

  useEffect(() => {
    getAllStaffIncludingPaused()
      .then(list => setAllStaff(
        list.filter(s =>
          (s.active !== false || s.temporaryDeactivation) &&
          (s.role || '').toLowerCase() !== 'operations'
        )
      ))
      .catch(console.error);
  }, []);

  // Convert MM-DD-YYYY to Date
  const dateObj = (() => {
    const [m, d, y] = selectedDate.split('-');
    const year = y.length === 2 ? 2000 + parseInt(y) : parseInt(y);
    return new Date(year, parseInt(m) - 1, parseInt(d));
  })();

  // Date input needs YYYY-MM-DD
  const dateInputVal = (() => {
    const [m, d, y] = selectedDate.split('-');
    const year = y.length === 2 ? 2000 + parseInt(y) : y;
    return `${year}-${m}-${d}`;
  })();

  return (
    <div style={{ padding: '1rem', maxWidth: '960px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.25rem' }}>
        <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', color: '#374151', marginBottom: '0.75rem' }}>
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h1 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1f2937', margin: 0 }}>📋 Daily Duties</h1>
            <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0.2rem 0 0' }}>Break schedules and duty assignments — Redmond & Kirkland</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#374151' }}>Date</label>
            <input type="date" value={dateInputVal}
              onChange={e => {
                const [y, m, d] = e.target.value.split('-');
                setSelectedDate(`${m}-${d}-${y}`);
              }}
              style={{ padding: '0.45rem 0.65rem', border: '1.5px solid #d1d5db', borderRadius: '7px', fontSize: '0.875rem' }}
            />
          </div>
        </div>
      </div>

      {/* Two location panels */}
      {LOCATIONS.map(loc => (
        <LocationPanel
          key={loc.id}
          locConfig={loc}
          allStaff={allStaff}
          dateStr={selectedDate}
          dateObj={dateObj}
        />
      ))}
    </div>
  );
};

export default DailyDuties;